import { unformat, formatMoney, formatNumber, toFixed } from 'accounting';
import { get, startCase } from 'lodash';
import { FinanceType } from './legos/constants';
import { DeskingOffer } from './legos/deskingTypes';
import { isDefined } from './legos/utils/tsutils';

// Remove all non-numericals
const onlyNumbers = (str: string) => {
  const numbers = str.replace(/\D+/g, '');
  return numbers;
};

// Assumes phone of 310-555-7777 format
export const fmtPhone = onlyNumbers;

// Performs the exact same transformations.
export const fmtSocial = onlyNumbers;

// Assumes date of 11/10/1985 format
export const fmtDate = (date: string) =>
  new Date(date).toISOString().split('T')[0];

// Assumes date of 11/10/2015 format
// http://stackoverflow.com/questions/2536379/difference-in-months-between-two-dates-in-javascript
export const fmtMonthsFromStart = (startStr: string) => {
  const now = new Date();
  const start = new Date(startStr);
  let months = (now.getFullYear() - start.getFullYear()) * 12;
  months -= start.getMonth();
  months += now.getMonth();
  return months <= 0 ? 0 : months;
};

// Takes a percent and formats it to a decimal.
export const fmtFromPercent = (percent: number) =>
  unformat(toFixed(0.01 * percent, 4));

export const fmtPrice = (price: number | string) => formatMoney(price);

// No decimals
export const fmtPriceSimple = (price: number | string) =>
  formatMoney(price, { precision: 0 });

export const fmtPercent = (percent: number, precision = 2): string =>
  `${toFixed(100 * percent, precision)}%`;

export const optionalFmtPercent = (
  percent: number | undefined,
  precision = 2,
): string | undefined =>
  isDefined(percent) ? fmtPercent(percent, precision) : percent;

export const fmtMoney = (price: number, precision = 0): string => {
  if (price < 0) {
    return `-${formatMoney(Math.abs(price), { precision })}`;
  }
  return formatMoney(price, { precision });
};

export const optionalFmtMoney = (
  price: number | undefined,
  precision = 0,
): string | undefined =>
  isDefined(price) ? fmtMoney(price, precision) : price;

export const fmtPropNameForTaxUpdate = (
  financeType: FinanceType,
  pencil: any,
  offerIndex: number,
): string => {
  if (financeType === FinanceType.LEASE) {
    const mileage = get(pencil, `data.${financeType}[${offerIndex}].mileage`);
    return `rebate ${mileage} miles`;
  }
  return 'rebate';
};

export const fmtRebatePropName = (deskingOffer: DeskingOffer): string => {
  if (deskingOffer.data?.financeType === FinanceType.LEASE) {
    const mileage = get(deskingOffer, 'data.mileage');
    return `rebate ${mileage} miles`;
  }
  return 'rebate';
};

export const fmtTaxes = (value: number, proptype: string): string | number => {
  if (proptype === 'rate') {
    return fmtPercent(value);
  }
  return proptype === 'name' ? value : fmtMoney(value);
};

export const optionalFmtTaxes = (
  value: number | undefined,
  proptype: string,
): string | number | undefined =>
  isDefined(value) ? fmtTaxes(value, proptype) : value;

export const fmtNumber = (number: number): string => formatNumber(number);

export const fmtCountDown = (amount: number) => {
  // amount is in seconds
  let result = '';
  const hasMinutes = amount >= 60;
  if (hasMinutes) {
    result += `${Math.floor(amount / 60)}:`;
  }
  result += `${amount % 60}`.padStart(hasMinutes ? 2 : 1, '0');
  return result;
};

/**
 * Normalize county by converting it to lowercase, splitting by dash,
 * applying start case function, and then joining them back together with dashes.
 */
export function formatCounty(county: string): string {
  return county
    .toLowerCase()
    .split('-')
    .map(startCase)
    .join('-');
}

export const getMilesWithComma = (miles: number): string => {
  const stringMiles = miles.toString(10);
  if (miles > 1000) {
    return `${stringMiles.slice(0, -3)},${stringMiles.slice(-3)}`;
  }
  return stringMiles;
};

export type FinancingInformationField =
  | 'apr'
  | 'financeAmount'
  | 'monthly'
  | 'downPayment'
  | 'months';

export const formatFinanceValue = (
  value: number,
  field: FinancingInformationField
) => {
  let fmtFn;
  if (field === 'apr') {
    fmtFn = (val?: number) => fmtPercent(val ?? 0, 2);
  } else if (field === 'months') {
    fmtFn = (val?: number) => `${val ?? 0} months`;
  } else {
    fmtFn = (val?: number) => fmtMoney(val ?? 0, 2);
  }

  return fmtFn(value);
};
